import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * Floating "Teach the matcher" widget.
 *
 * Walks through every prediction the availability cache could NOT
 * resolve, asks `/api/betway/aliases/pending` for the closest Betway
 * candidate, then prompts the user "Is `PSG` the same team as
 * `Paris Saint Germain`?" — Yes / No / Skip.
 *
 * Confirmed Yes/No decisions are POSTed to `/api/betway/aliases/confirm`
 * and persisted in MongoDB; they take effect on the matcher immediately
 * so the next page refresh shows the previously-missing ✓ On Betway
 * badge.
 *
 * Props:
 *   predictions: array — same predictions list used by HomePage
 *   availability: object — output of `useBetwayAvailability`
 */
export const AliasLearningWidget = ({ predictions, availability }) => {
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState([]);     // [{prompt, fixture}]
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const buildQueue = useCallback(async () => {
    setLoading(true);
    const candidates = (predictions || []).filter((p) => {
      const id = p?.match?.fixture_id || p?.match?.id;
      const a = availability?.[String(id)];
      // Only ask about fixtures that are explicitly marked unavailable
      // (false) — null = league not in cache, skip those.
      return a === false;
    });
    const out = [];
    for (const p of candidates) {
      const home = p?.match?.home_team?.name;
      const away = p?.match?.away_team?.name;
      const league = p?.match?.league?.name;
      if (!home || !away || !league) continue;
      try {
        const { data } = await axios.get(
          `${API}/betway/aliases/pending`,
          { params: { home, away, league } }
        );
        for (const prompt of data.prompts || []) {
          out.push({
            fixture: { home, away, league },
            prompt,
            betway_match: data.betway_match,
          });
        }
      } catch (_) {
        // skip errors silently
      }
      if (out.length >= 12) break;       // soft cap so the loop is fast
    }
    setQueue(out);
    setIdx(0);
    setLoading(false);
  }, [predictions, availability]);

  // Run the one-shot backfill: auto-confirm obvious matches and queue
  // ambiguous ones for review.  Triggered by the user-facing button.
  const runBackfill = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/betway/aliases/backfill`,
        { dry_run: false }
      );
      const reviewQueue = (data.needs_review || []).map((r) => ({
        fixture: { home: r.source_name, away: "—", league: r.league },
        prompt: {
          source_name: r.source_name,
          betway_name: r.betway_name,
          league: r.league,
          score: r.score,
        },
        betway_match: null,
      }));
      setQueue(reviewQueue);
      setIdx(0);
      // Surface the auto-confirm count so the user sees instant value.
      const n = (data.auto_confirmed || []).length;
      if (n > 0 && typeof window !== "undefined") {
        try {
          // eslint-disable-next-line no-alert
          window.alert(
            `Auto-confirmed ${n} obvious team-name aliases.  ` +
            `${reviewQueue.length} ambiguous matches queued for your review.`
          );
        } catch (_) {}
      }
    } catch (_) {
      // ignore
    }
    setLoading(false);
  };

  // Auto-build the queue once we have data — but DON'T auto-open.
  useEffect(() => {
    if (!predictions || !availability) return;
    if (Object.keys(availability).length === 0) return;
    buildQueue();
  }, [predictions, availability, buildQueue]);

  const submit = async (same) => {
    const item = queue[idx];
    if (!item) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/betway/aliases/confirm`, {
        source_name: item.prompt.source_name,
        betway_name: item.prompt.betway_name,
        same,
      });
    } catch (_) {
      // ignore — UX continues regardless
    }
    setSubmitting(false);
    next();
  };

  const next = () => {
    setIdx((i) => i + 1);
  };

  const total = queue.length;
  const remaining = total - idx;

  // Floating launcher button — show whenever we have predictions and a
  // healthy availability cache, so the user can trigger the one-shot
  // backfill even if no per-fixture prompts have been queued yet.
  if (!open) {
    const hasPredictions = (predictions || []).length > 0;
    if (total === 0 && !hasPredictions) return null;
    return (
      <button
        data-testid="alias-learning-launcher"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-600/30 hover:bg-emerald-500 transition"
        title="Teach the matcher about team-name aliases"
      >
        <span>🎓</span>
        <span>{total > 0 ? `Help train (${remaining})` : "Train matcher"}</span>
      </button>
    );
  }

  // Modal-ish panel
  const item = queue[idx];

  return (
    <div
      data-testid="alias-learning-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
    >
      <div className="w-full max-w-lg rounded-2xl bg-zinc-900 border border-zinc-700 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Teach the matcher
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              Help us link prediction-source team names to Betway's
              listings. {remaining > 0 ? `${remaining} left.` : ""}
            </p>
          </div>
          <button
            data-testid="alias-learning-close"
            onClick={() => setOpen(false)}
            className="text-zinc-500 hover:text-zinc-300 text-xl leading-none"
          >
            ×
          </button>
        </div>

        {loading && (
          <p className="text-zinc-400 text-sm">Loading questions…</p>
        )}

        {!loading && !item && (
          <div className="py-6 text-center">
            <p className="text-zinc-300 mb-4">
              No per-fixture prompts in the queue.
            </p>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed max-w-sm mx-auto">
              Run a one-shot backfill across every team in the
              predictions database — auto-confirms the obvious matches
              and queues the ambiguous ones for you to review.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                data-testid="alias-backfill-btn"
                onClick={runBackfill}
                className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-5 py-2 text-sm font-semibold text-white"
              >
                🚀 Run quick import
              </button>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full bg-zinc-700 hover:bg-zinc-600 px-4 py-2 text-sm text-white"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {!loading && item && (
          <div>
            <div className="rounded-lg bg-zinc-800/70 border border-zinc-700 px-4 py-4 mb-5">
              <p className="text-sm text-zinc-400 mb-1">
                Fixture:{" "}
                <span className="text-zinc-200">
                  {item.fixture.home} vs {item.fixture.away}
                </span>{" "}
                <span className="text-zinc-500">({item.fixture.league})</span>
              </p>
              <p className="text-base text-white mt-3">
                Is{" "}
                <span className="font-semibold text-amber-300">
                  {item.prompt.source_name}
                </span>{" "}
                the same team as{" "}
                <span className="font-semibold text-emerald-300">
                  {item.prompt.betway_name}
                </span>
                ?
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                Similarity score: {(item.prompt.score * 100).toFixed(0)}%
              </p>
            </div>

            <div className="flex gap-3">
              <button
                data-testid="alias-yes"
                disabled={submitting}
                onClick={() => submit(true)}
                className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-4 py-3 text-sm font-semibold text-white transition"
              >
                Yes — same team
              </button>
              <button
                data-testid="alias-no"
                disabled={submitting}
                onClick={() => submit(false)}
                className="flex-1 rounded-full bg-rose-600 hover:bg-rose-500 disabled:opacity-50 px-4 py-3 text-sm font-semibold text-white transition"
              >
                No — different
              </button>
              <button
                data-testid="alias-skip"
                disabled={submitting}
                onClick={next}
                className="rounded-full bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 px-4 py-3 text-sm font-medium text-zinc-200 transition"
              >
                Skip
              </button>
            </div>

            <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
              Tip — saying <span className="text-emerald-300">Yes</span> teaches
              the system to treat both names as the same team forever, so the
              next time this fixture appears it will show ✓ On Betway and the
              parlay bot will jump straight to it.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AliasLearningWidget;
